import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Merges Tailwind classes, letting a caller's class win over a component's
 * default without both ending up in the output.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Millionths of a currency unit — the integer the API books cost in.
 *
 * Totals read fine at cents; a single image costs a fraction of a dollar, so
 * per-image figures pass more digits to keep differences between
 * configurations visible.
 */
export function formatMicros(micros: number | null | undefined, maximumFractionDigits = 2): string {
  if (micros == null) return '—'
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits,
  }).format(micros / 1_000_000)
}

export function formatNumber(value: number | null | undefined): string {
  if (value == null) return '—'
  return new Intl.NumberFormat().format(value)
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? '—'
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

/** Stopwatch-style "m:ss" since `from`, for work that is still running. */
export function formatElapsed(from: string, now: number = Date.now()): string {
  const start = new Date(from).getTime()
  if (Number.isNaN(start)) return '—'
  const total = Math.max(0, Math.floor((now - start) / 1000))
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export function formatRelative(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'

  const seconds = Math.round((date.getTime() - Date.now()) / 1000)
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 30],
    ['month', 12],
    ['year', Number.POSITIVE_INFINITY],
  ]

  let value_ = seconds
  for (const [unit, limit] of units) {
    if (Math.abs(value_) < limit) {
      return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(Math.round(value_), unit)
    }
    value_ /= limit
  }
  return formatDate(value)
}
